import type { Rol } from '@condo/contracts';

/**
 * Sesión autenticada — shape común entre el auth mock (MSW) y el auth real
 * (Cognito). El frontend nunca distingue entre los dos, solo consume esta
 * forma. El día que se reemplace el mock por integración real con Amplify o
 * un SDK alternativo, ningún componente que lea `AuthSession` cambia.
 *
 * NOTA (multi-tenant): `tenantId` y `rol` se eliminaron. El rol efectivo se
 * deriva en cada contexto de uso como `session.tenants[condoId]` donde
 * `condoId` viene del param `:condoId` de la URL. `isSuperAdmin` identifica
 * el rol de plataforma ortogonal a los tenants.
 */
export interface AuthSession {
  userId: string;
  email: string;
  /** Nombre para display (claim `name` del token). */
  nombre: string;
  isSuperAdmin: boolean;
  /** Mapa tenantId → rol del usuario en ese condominio. */
  tenants: Record<string, Rol>;
  /**
   * Id token JWT. En el modo mock es un string determinístico no firmado.
   * En modo real es el `idToken.jwtToken` devuelto por Cognito.
   */
  token: string;
  /** ISO timestamp de expiración del token. */
  expiresAt: string;
}

/**
 * Puerto del proveedor de auth. Dos implementaciones:
 *   - `MockAuthProvider` (lib/auth/cognito-mock.ts) — in-memory, para MSW.
 *   - `CognitoAuthProvider` (lib/auth/cognito-client.ts) — Cognito real.
 *
 * La elección se hace en `lib/auth/index.ts` leyendo `VITE_USE_MOCKS`.
 */
export interface AuthProvider {
  signIn(email: string, password: string): Promise<AuthSession>;
  signOut(): Promise<void>;
  /** Intenta recuperar la sesión actual desde almacenamiento persistente. */
  restoreSession(): Promise<AuthSession | null>;
}

export class AuthError extends Error {
  constructor(
    public readonly code:
      | 'invalid_credentials'
      | 'user_not_found'
      | 'new_password_required'
      | 'network_error'
      | 'unknown',
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'AuthError';
  }
}
